/** Staff accounts of a site: invite by email with a role, change role, remove. Route /sites/:id/staff (or a Settings tab). */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Empty, Input, Popconfirm, Result, Select, Space, Table, Tag, Typography, message } from 'antd';
import { DeleteOutlined, UserAddOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { QUIET } from './api';
import { fmtDate, listOf, toastErr, useSite } from './common';
import { PlansModal, showPlans } from './Media';

const { Text } = Typography;

export const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', editor: 'Editor', orders: 'Orders', marketing: 'Marketing' };
const ROLE_HINT = {
    admin: 'Everything except payments and staff',
    editor: 'Pages, content and media',
    orders: 'Orders, customers and gift cards',
    marketing: 'Discounts, reviews, feeds and marketing',
};

const ShopStaff = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [d, setD] = useState(null);
    const [soon, setSoon] = useState(false);
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('editor');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try { setD(await api.get('/staff', null, QUIET)); } catch (e) { if (e?.status === 404) setSoon(true); else toastErr(e); setD({ items: [] }); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const roles = (d?.roles || Object.keys(ROLE_LABELS)).filter((r) => r !== 'owner');
    const roleOptions = roles.map((r) => ({ value: r, label: <span>{t(ROLE_LABELS[r] || r)} {ROLE_HINT[r] && <Text type="secondary" style={{ fontSize: 12 }}>· {t(ROLE_HINT[r])}</Text>}</span>, short: t(ROLE_LABELS[r] || r) }));

    const invite = async () => {
        if (!email.trim()) return;
        setBusy(true);
        try {
            await api.post('/staff', { email: email.trim(), role }, QUIET);
            message.success(t('Invitation sent'));
            setEmail('');
            load();
        } catch (e) {
            if (e?.body?.code === 'plan_limit' || e?.status === 402) {
                message.warning({ content: <Space>{e.body?.error || e.error || t('Your plan has no free staff seats')}<Button size="small" type="primary" onClick={showPlans}>{t('Upgrade')}</Button></Space> });
            } else toastErr(e);
        } finally { setBusy(false); }
    };
    const setStaffRole = async (s, r) => {
        try { await api.put(`/staff/${s.id}`, { role: r }); load(); } catch (e) { toastErr(e); }
    };
    const remove = async (s) => {
        try { await api.del(`/staff/${s.id}`); load(); } catch (e) { toastErr(e); }
    };

    if (soon) return <Card><Result status="info" title={t('Staff accounts are coming soon')} /></Card>;
    const seats = d?.seats;
    const canManage = !d?.my_role || ['account_owner', 'owner', 'admin'].includes(d.my_role);
    const full = seats && Number(seats.limit) >= 0 && Number(seats.used) >= Number(seats.limit);
    return (
        <Card size="small" title={t('Staff')} extra={seats && <Text type="secondary">{t('{{used}} / {{limit}} seats', { used: seats.used, limit: Number(seats.limit) < 0 ? '∞' : seats.limit })}</Text>}>
            {canManage && (
                <Space.Compact style={{ width: '100%', maxWidth: 640, marginBottom: 16 }}>
                    <Input placeholder="name@example.com" value={email} onChange={(e) => setEmail(e.target.value)} onPressEnter={invite} />
                    <Select value={role} onChange={setRole} options={roleOptions} optionLabelProp="short" style={{ width: 160 }} popupMatchSelectWidth={340} />
                    <Button type="primary" icon={<UserAddOutlined />} loading={busy} onClick={invite} disabled={full}>{t('Invite')}</Button>
                </Space.Compact>
            )}
            {canManage && full && (
                <div style={{ marginTop: -8, marginBottom: 16 }}>
                    <Text type="secondary">{t('All staff seats on your plan are in use.')}</Text> <a onClick={showPlans}>{t('Upgrade')}</a>
                </div>
            )}
            <PlansModal />
            <Table rowKey={(r) => r.id || 'owner'} size="small" loading={!d} pagination={false} scroll={{ x: 560 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('Only you have access')} /> }}
                dataSource={[...(d?.owner ? [{ id: 0, email: d.owner.email, username: d.owner.username, role: 'owner', status: 'active' }] : []), ...listOf(d)]}
                columns={[
                    { title: t('Person', { context: 'shop' }), key: 'p', render: (_, s) => <Space direction="vertical" size={0}><Text>{s.username || s.email}</Text>{s.username && <Text type="secondary" style={{ fontSize: 12 }}>{s.email}</Text>}</Space> },
                    {
                        title: t('Role'), dataIndex: 'role', width: 180, render: (v, s) => (v === 'owner' || !canManage ? <Tag>{t(ROLE_LABELS[v] || v)}</Tag>
                            : <Select size="small" value={v} onChange={(r) => setStaffRole(s, r)} options={roleOptions} optionLabelProp="short" style={{ width: 150 }} popupMatchSelectWidth={340} />),
                    },
                    { title: t('Status'), dataIndex: 'status', width: 120, render: (v, s) => (v === 'invited' ? <Tag color="gold">{t('Invited')}</Tag> : s.role === 'owner' ? null : <Tag color="green">{t('Active')}</Tag>) },
                    { title: t('Since', { context: 'shop' }), key: 'at', width: 160, render: (_, s) => fmtDate(s.accepted_at || s.created_at, false) },
                    {
                        title: '', key: 'x', width: 50, render: (_, s) => (s.role !== 'owner' && canManage ? (
                            <Popconfirm title={t('Remove access?')} onConfirm={() => remove(s)}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm>
                        ) : null),
                    },
                ]} />
        </Card>
    );
};

export default ShopStaff;
